import { useState, useEffect, useCallback } from 'react';
import { locationDetectionAPI } from '../services/api';

const useLocationDetection = () => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('unknown');

  const checkPermission = useCallback(async () => {
    if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(permission.state);
        return permission.state === 'granted';
      } catch (err) {
        console.warn('Permission check failed:', err);
        return false;
      }
    }
    return false;
  }, []);

  const requestPermission = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          () => {
            setPermissionStatus('granted');
            resolve(true);
          },
          (err) => {
            setPermissionStatus('denied');
            reject(err);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000
          }
        );
      } else {
        reject(new Error('Geolocation not supported'));
      }
    });
  }, []);

  const getDeviceLocation = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      throw new Error('Geolocation not supported');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const response = await locationDetectionAPI.detect(
              {
                coordinates: {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude
                }
              },
              { timeout: 15000 }
            );
            resolve(response.data.data);
          } catch (err) {
            reject(err);
          }
        },
        (err) => {
          reject(err);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000
        }
      );
    });
  }, []);

  const getIPLocation = useCallback(async () => {
    try {
      const response = await locationDetectionAPI.detect(
        {
          useDeviceLocation: false,
          useIPLocation: true,
          useTimezoneLocation: true
        },
        { timeout: 10000 }
      );

      if (response.data && response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error('Invalid response from location detection service');
    } catch (err) {
      console.error('❌ IP location detection error:', err);
      if (err.response) {
        throw new Error(
          `IP location detection failed: ${err.response.data?.message || err.response.statusText || 'Server error'}`
        );
      }
      if (err.request) {
        throw new Error(
          'IP location detection failed: No response from server. Please check your connection.'
        );
      }
      throw new Error(`IP location detection failed: ${err.message || 'Unknown error'}`);
    }
  }, []);

  const detectLocation = useCallback(
    async (options = {}) => {
      const {
        useDeviceLocation = false,
        useIPLocation = true,
        forceRefresh = false
      } = options;

      if (!forceRefresh) {
        const cached = localStorage.getItem('user_location');
        if (cached) {
          try {
            const parsedCache = JSON.parse(cached);
            const cacheAge = Date.now() - parsedCache.timestamp;
            if (cacheAge < 5 * 60 * 1000) {
              setLocation(parsedCache.data);
              return parsedCache.data;
            }
          } catch {
            // Invalid cache, continue
          }
        }
      }

      setLoading(true);
      setError(null);

      try {
        let detectedLocation = null;

        if (useDeviceLocation && permissionStatus === 'granted') {
          try {
            detectedLocation = await getDeviceLocation();
            console.log('📍 Location detected using device GPS');
          } catch (err) {
            console.warn('Device location failed:', err.message);
          }
        }

        if (!detectedLocation && useIPLocation) {
          try {
            detectedLocation = await getIPLocation();
            console.log('📍 Location detected using IP geolocation');
          } catch (err) {
            console.error('IP location failed:', err);
            setError(`IP location detection failed: ${err.message || 'Unknown error'}`);
            throw new Error(`IP location detection failed: ${err.message || 'Unknown error'}`);
          }
        }

        if (!detectedLocation) {
          const errorMsg =
            'All location detection methods failed. Please try again or select manually.';
          setError(errorMsg);
          throw new Error(errorMsg);
        }

        try {
          localStorage.setItem(
            'user_location',
            JSON.stringify({
              data: detectedLocation,
              timestamp: Date.now()
            })
          );
        } catch (e) {
          console.warn('Failed to cache location:', e);
        }

        setLocation(detectedLocation);
        return detectedLocation;
      } catch (err) {
        console.error('Location detection failed:', err);
        const errorMsg = err.message || 'Location detection failed. Please try again or select manually.';
        setError(errorMsg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [permissionStatus, getDeviceLocation, getIPLocation]
  );

  const getLocationSuggestions = useCallback(async (query, type = 'all') => {
    try {
      const response = await locationDetectionAPI.getSuggestions({
        params: { query, type, limit: 10 }
      });
      return response.data.data;
    } catch (err) {
      console.error('Failed to get location suggestions:', err);
      return [];
    }
  }, []);

  const getLocationHierarchy = useCallback(async (countryId = null, stateId = null) => {
    try {
      const response = await locationDetectionAPI.getHierarchy({
        params: { countryId, stateId }
      });
      return response.data.data;
    } catch (err) {
      console.error('Failed to get location hierarchy:', err);
      return [];
    }
  }, []);

  const clearCache = useCallback(() => {
    localStorage.removeItem('user_location');
    setLocation(null);
    console.log('📍 Location cache cleared');
  }, []);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return {
    location,
    loading,
    error,
    permissionStatus,
    detectLocation,
    getDeviceLocation,
    getIPLocation,
    getLocationSuggestions,
    getLocationHierarchy,
    requestPermission,
    checkPermission,
    clearCache
  };
};

export default useLocationDetection;
